import type { UIRect } from '@poseidon/types/types/types';

import { cn } from '@poseidon/components/cn';
import { Tooltip } from '@poseidon/components/Input2';
import { General } from '@poseidon/components/StrokeIcons';

import { anchorPt, getMidpoint2, squarePath2 } from '@/clientOnlyUtils/graph.utils';
import { candidateStatusColor } from '@/clientOnlyUtils/humanizeCandidateStatus';
import type { FlowBranch, FlowNode } from './candidateFlow.graph';
import { CONTENT_H, CONTENT_W, FLOW_EDGES, FLOW_NODES, NODE_H, NODE_W } from './candidateFlow.graph';

type AccentColor = 'red' | 'green' | 'blue' | 'yellow' | 'gray';

// Accent classes per Button-style color, shared shape: an outline ring, a count
// badge, and a status dot. Mirrors the red = bad / green = enrolled / blue =
// in-flight / yellow = needs-attention semantics used across the app.
const ACCENT: Record<AccentColor, { ring: string; badge: string; dot: string }> = {
  gray: {
    ring: 'border-gray-200 dark:border-gray-700',
    badge: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
    dot: 'bg-gray-300 dark:bg-gray-600',
  },
  blue: {
    ring: 'border-blue-300 dark:border-blue-500/50',
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300',
    dot: 'bg-blue-400',
  },
  green: {
    ring: 'border-green-400 dark:border-green-500/60',
    badge: 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300',
    dot: 'bg-green-500',
  },
  yellow: {
    ring: 'border-yellow-300 dark:border-yellow-500/50',
    badge: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300',
    dot: 'bg-yellow-400',
  },
  red: {
    ring: 'border-red-300 dark:border-red-500/50',
    badge: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300',
    dot: 'bg-red-400',
  },
};

const rectOf = (n: FlowNode): UIRect => ({ x: n.x, y: n.y, w: n.w ?? NODE_W, h: n.h ?? NODE_H });

const accentOf = (n: FlowNode): AccentColor => {
  if (n.status) return candidateStatusColor[n.status] ?? 'gray';
  return 'gray';
};

// A branch is dimmed when this template doesn't take it: the personalized path
// only when there are no personalized steps, the bypass only when there are,
// and the filter-failure edge only when there are no filter rules.
const branchDimmed = (branch: FlowBranch | undefined, hasFilterRules: boolean, hasPersonalizedSteps: boolean): boolean => {
  if (branch === 'personalized') return !hasPersonalizedSteps;
  if (branch === 'noPersonalization') return hasPersonalizedSteps;
  if (branch === 'filter') return !hasFilterRules;
  return false;
};

// Longer, hover-only explanation of each state. Keyed by node id so the graph
// topology (candidateFlow.graph.ts) stays about structure and this stays about
// copy. Falls back to the node's subtitle if an id is missing.
const NODE_DESC: Record<string, string> = {
  entry: 'How people enter this campaign: a bulk add, a Sales Navigator import, a single contact, or a webhook.',
  pending: 'Added and queued — waiting for a worker to pick them up.',
  researching: "Fetching the person's LinkedIn profile.",
  gathering_sources: 'Searching the web for background on the person (via Tavily).',
  synthesizing: 'Using AI to synthesize a research dossier from the gathered sources.',
  filter: "Checks the person against this campaign's filter rules to decide whether they qualify.",
  personalizing: 'Generating personalized message drafts for this person with AI.',
  pending_review: 'Waiting for a human to review the drafts and approve or reject.',
  promoted: 'Enrolled — an active campaign (cadence) has been created for this person.',
  removed: 'A user backed this person out of the candidate list.',
  research_failed: "Couldn't reach the LinkedIn profile (no identifier, contact missing, or the lookup failed).",
  synthesize_failed: 'The AI dossier synthesis errored out.',
  filter_failed: "The person didn't match this campaign's filter rules.",
  personalize_failed: 'Every personalized draft failed to generate.',
  rejected: 'A reviewer rejected the person, or they were already enrolled in another campaign.',
  rate_limited: 'Temporarily paused after hitting a rate limit — resumes automatically.',
  account_disconnected: 'Paused because the LinkedIn account is disconnected — resumes on reconnect.',
  awaiting_capacity: 'Waiting for processing capacity — resumes automatically.',
  retrying: 'A transient error occurred — retrying with backoff.',
};

// Each blocked pause state is triggered from a specific processing step: which
// step it hangs off (BLOCKED_SOURCE) and the event that sends a person there
// (BLOCKED_TRIGGER, written on the trigger arrow). It then auto-resumes back to
// that same step.
// `awaiting_capacity` is intentionally absent — it's wired to the concurrency
// gate (gate full → wait → retry), not to a step. See `capacityLink` below.
const BLOCKED_SOURCE: Record<string, string> = {
  account_disconnected: 'researching',
  rate_limited: 'gathering_sources',
  retrying: 'synthesizing',
};
const BLOCKED_TRIGGER: Record<string, string> = {
  awaiting_capacity: 'no free slot',
  account_disconnected: 'connection lost',
  rate_limited: 'hits rate limit',
  retrying: 'hits an error',
};

// Why a branch is unused (and its path greyed) for this specific campaign.
const dimReason = (branch: FlowBranch | undefined): string =>
  branch === 'personalized'
    ? 'there are no personalized steps, so people enroll straight from the filter check without drafting or review'
    : branch === 'filter'
      ? 'there are no filter rules, so no one is filtered out'
      : "this branch isn't used";

const NODE_BY_ID = new Map(FLOW_NODES.map((n) => [n.id, n]));
const EDGE_OFFSET = 24;
// Per-team concurrency cap — UNIPILE_TEAM_CAP / TAVILY_TEAM_CAP in
// packages/server/src/apiWorker/resourceSlot.util.ts (both 5).
const CONCURRENCY_CAP = 5;

// The two concurrency gates: which step each caps, the entry transition it sits
// on (`fromId` → `stepId`), and the pool behind it.
const CAP_DEFS = [
  {
    id: 'unipile',
    stepId: 'researching',
    fromId: 'pending',
    pool: 'Unipile (LinkedIn)',
    inLabel: 'picked up',
    outLabel: 'slot free',
    tip: `Only ${CONCURRENCY_CAP} candidates per team can be in Researching at once — a LinkedIn/Unipile slot is grabbed before a candidate leaves Pending (shared with campaign message sends), then freed when it moves to Searching the web. When the pool is full, the candidate stays in Pending and is retried with backoff.`,
  },
  {
    id: 'tavily',
    stepId: 'gathering_sources',
    fromId: 'researching',
    pool: 'Tavily (web)',
    inLabel: 'profile found',
    outLabel: 'slot free',
    tip: `Only ${CONCURRENCY_CAP} candidates per team can be Searching the web at once (Tavily). When the pool is full, the candidate briefly waits (Waiting for capacity) and auto-retries.`,
  },
];

interface Props {
  counts?: Partial<Record<string, number>>;
  hasFilterRules: boolean;
  hasPersonalizedSteps: boolean;
}

export function CandidateFlowGraph({ counts, hasFilterRules, hasPersonalizedSteps }: Props) {
  const total = Object.values(counts ?? {}).reduce((a, b) => (a ?? 0) + (b ?? 0), 0);

  // Each blocked pause state sits on its source step's row: a trigger arrow runs
  // from the step into the pause state (labelled with the event) and a resume
  // arrow runs back. Fully traceable — no abstract "in-flight" node.
  const detours = FLOW_NODES.filter((n) => n.kind === 'blocked').flatMap((n) => {
    const src = NODE_BY_ID.get(BLOCKED_SOURCE[n.id] ?? '');
    if (src == null) return [];
    const r = rectOf(n);
    // Most pause states auto-resume; a disconnected account needs the user to
    // reconnect, so that resume is a user action.
    const resumeUser = n.id === 'account_disconnected';
    return [
      {
        id: n.id,
        boxRight: r.x + r.w,
        stepLeft: rectOf(src).x,
        cy: r.y + r.h / 2,
        trigger: BLOCKED_TRIGGER[n.id] ?? '',
        resumeUser,
        resumeLabel: resumeUser ? 'reconnect' : 'resumes',
      },
    ];
  });

  // Per-team concurrency caps (UNIPILE_TEAM_CAP / TAVILY_TEAM_CAP = 5): a gate
  // node sits inline on the entry edge of each capped step — only 5 candidates
  // per team can be in that step at once; when the pool is full they wait
  // (Waiting for capacity). Centered in the gap above the step (see GATE_GAP).
  const gates = CAP_DEFS.flatMap((c) => {
    const step = NODE_BY_ID.get(c.stepId);
    const from = NODE_BY_ID.get(c.fromId);
    if (step == null || from == null) return [];
    const sr = rectOf(step);
    const fr = rectOf(from);
    const w = 172;
    const h = 40;
    const cx = sr.x + sr.w / 2; // spine center — the gate + its in/out arrows
    return [
      {
        id: c.id,
        pool: c.pool,
        tip: c.tip,
        inLabel: c.inLabel,
        outLabel: c.outLabel,
        cx,
        inFromY: fr.y + fr.h, // bottom of the source step (arrow enters the gate)
        outToY: sr.y, // top of the gated step (arrow leaves the gate)
        box: { x: cx - w / 2, y: (fr.y + fr.h + sr.y) / 2 - h / 2, w, h },
      },
    ];
  });

  // "Waiting for capacity" is the TAVILY gate being full (only the web-search
  // slot parks the row as awaiting_capacity; the Unipile gate full just leaves it
  // Pending to retry). Wire it to that gate: full → no free slot → wait → resumes.
  const capacityNode = NODE_BY_ID.get('awaiting_capacity');
  const tavilyGate = gates.find((g) => g.id === 'tavily');
  const capacityLink =
    capacityNode != null && tavilyGate != null
      ? (() => {
          const cr = rectOf(capacityNode);
          return { boxRight: cr.x + cr.w, gateLeft: tavilyGate.box.x, cy: cr.y + cr.h / 2 };
        })()
      : null;

  return (
    <div className="relative mx-auto shrink-0" style={{ width: CONTENT_W, height: CONTENT_H }}>
      <svg
        className="pointer-events-none absolute inset-0"
        width={CONTENT_W}
        height={CONTENT_H}
        viewBox={`0 0 ${CONTENT_W} ${CONTENT_H}`}>
        <defs>
          <marker
            id="cf-arrow"
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" className="fill-gray-300 dark:fill-gray-600" />
          </marker>
          <marker
            id="cf-arrow-user"
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" className="fill-violet-400" />
          </marker>
        </defs>
        {FLOW_EDGES.map((edge, i) => {
          const from = NODE_BY_ID.get(edge.from);
          const to = NODE_BY_ID.get(edge.to);
          if (from == null || to == null) return null;
          const fSide = edge.fromSide ?? 'down';
          const tSide = edge.toSide ?? 'up';
          const fromPt = anchorPt(fSide, rectOf(from), 0, 1);
          const toPt = anchorPt(tSide, rectOf(to), 0, 1);
          const d = squarePath2({ from: fromPt, to: toPt, fNormal: fSide, tNormal: tSide, offset: EDGE_OFFSET });
          const userAction = edge.userAction === true;
          return (
            <path
              key={i}
              d={d}
              fill="none"
              className={userAction ? 'stroke-violet-400' : 'stroke-gray-300 dark:stroke-gray-600'}
              strokeWidth={1.5}
              markerEnd={userAction ? 'url(#cf-arrow-user)' : 'url(#cf-arrow)'}
            />
          );
        })}

        {/* Concurrency-gate spine: arrow into the gate, arrow out to the step. */}
        {gates.map((g) => (
          <g key={g.id} className="stroke-gray-300 dark:stroke-gray-600" fill="none" strokeWidth={1.5}>
            <path d={`M ${g.cx} ${g.inFromY} L ${g.cx} ${g.box.y}`} markerEnd="url(#cf-arrow)" />
            <path d={`M ${g.cx} ${g.box.y + g.box.h} L ${g.cx} ${g.outToY}`} markerEnd="url(#cf-arrow)" />
          </g>
        ))}

        {/* Gate full → Waiting for capacity → resumes back to the gate. */}
        {capacityLink != null && (
          <g className="stroke-gray-300 dark:stroke-gray-600" fill="none" strokeWidth={1.5}>
            <path d={`M ${capacityLink.gateLeft} ${capacityLink.cy - 9} L ${capacityLink.boxRight} ${capacityLink.cy - 9}`} markerEnd="url(#cf-arrow)" />
            <path d={`M ${capacityLink.boxRight} ${capacityLink.cy + 9} L ${capacityLink.gateLeft} ${capacityLink.cy + 9}`} markerEnd="url(#cf-arrow)" />
          </g>
        )}

        {/* Blocked pause-states: a trigger arrow (step → pause) + a resume arrow
            (pause → step) for each, on the source step's row. */}
        <g className="stroke-gray-300 dark:stroke-gray-600" fill="none" strokeWidth={1.5}>
          {detours.map((d) => (
            <g key={d.id}>
              {/* trigger — the system hitting the snag */}
              <path d={`M ${d.stepLeft} ${d.cy - 9} L ${d.boxRight} ${d.cy - 9}`} markerEnd="url(#cf-arrow)" />
              {/* resume — violet when it needs the user (reconnect) */}
              <path
                d={`M ${d.boxRight} ${d.cy + 9} L ${d.stepLeft} ${d.cy + 9}`}
                className={d.resumeUser ? 'stroke-violet-400' : undefined}
                markerEnd={d.resumeUser ? 'url(#cf-arrow-user)' : 'url(#cf-arrow)'}
              />
            </g>
          ))}
        </g>

      </svg>

      {/* Edge labels — HTML so they pick up dark mode + a legible backplate. */}
      {FLOW_EDGES.map((edge, i) => {
        if (edge.label == null) return null;
        const from = NODE_BY_ID.get(edge.from);
        const to = NODE_BY_ID.get(edge.to);
        if (from == null || to == null) return null;
        const fSide = edge.fromSide ?? 'down';
        const tSide = edge.toSide ?? 'up';
        const fromPt = anchorPt(fSide, rectOf(from), 0, 1);
        const toPt = anchorPt(tSide, rectOf(to), 0, 1);
        const mid = getMidpoint2({ from: fromPt, to: toPt, fNormal: fSide, tNormal: tSide, offset: EDGE_OFFSET });
        const userAction = edge.userAction === true;
        return (
          <div
            key={i}
            className={cn(
              'absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded bg-white/85 px-1 text-[10px] leading-tight dark:bg-slate-900/85',
              userAction ? 'font-medium text-violet-600 dark:text-violet-400' : 'text-gray-400 dark:text-gray-500',
            )}
            style={{ left: mid.x, top: mid.y }}>
            {edge.label}
          </div>
        );
      })}

      {/* Trigger label (top arrow) on each pause-state detour. */}
      {detours.map((d) =>
        d.trigger === '' ? null : (
          <div
            key={`${d.id}-trigger`}
            className="absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded bg-white/85 px-1 text-[10px] leading-tight text-gray-500 dark:bg-slate-900/85 dark:text-gray-400"
            style={{ left: (d.boxRight + d.stepLeft) / 2, top: d.cy - 21 }}>
            {d.trigger}
          </div>
        ),
      )}
      {/* Resume label (bottom arrow) on each pause-state detour. */}
      {detours.map((d) => (
        <div
          key={`${d.id}-resume`}
          className={cn(
            'absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded bg-white/85 px-1 text-[10px] leading-tight dark:bg-slate-900/85',
            d.resumeUser ? 'font-medium text-violet-600 dark:text-violet-400' : 'text-gray-400 dark:text-gray-500',
          )}
          style={{ left: (d.boxRight + d.stepLeft) / 2, top: d.cy + 21 }}>
          {d.resumeLabel}
        </div>
      ))}

      {/* Nodes */}
      {FLOW_NODES.map((node) => {
        const rect = rectOf(node);
        const accent = ACCENT[accentOf(node)];
        const dimmed = branchDimmed(node.branch, hasFilterRules, hasPersonalizedSteps);
        const count = node.status ? (counts?.[node.status] ?? 0) : node.id === 'entry' ? total : undefined;
        const subtitle =
          node.id === 'filter'
            ? hasFilterRules
              ? 'Filter rules apply'
              : 'No filter rules — everyone passes'
            : node.subtitle;
        const description = NODE_DESC[node.id] ?? node.subtitle;
        // Terminal dead-ends only leave when a user re-adds the person.
        const isDeadEnd = node.kind === 'sinkFail';
        const base = dimmed ? `${description} Not used in this campaign — ${dimReason(node.branch)}.` : description;
        const tooltip = isDeadEnd ? `${base} Dead-end — a user must re-add the person to restart.` : base;
        return (
          <Tooltip
            as="div"
            key={node.id}
            tooltip={tooltip}
            tooltipMaxWidth={260}
            className={cn(
              'absolute flex cursor-help flex-col justify-center rounded-lg border bg-white px-3 py-1.5 shadow-sm dark:bg-slate-900',
              accent.ring,
              node.kind === 'sinkDone' && 'ring-2 ring-green-400/40',
            )}
            style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}>
            {isDeadEnd && (
              <span className="absolute -left-2 -top-2 flex items-center gap-0.5 rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] font-medium leading-none text-violet-600 ring-1 ring-violet-300 dark:bg-violet-500/20 dark:text-violet-300 dark:ring-violet-500/40">
                ↻ re-add
              </span>
            )}
            <div className="flex min-w-0 items-center gap-1.5">
              <span className={cn('size-1.5 shrink-0 rounded-full', accent.dot)} />
              <span className="truncate text-[13px] font-medium text-gray-900 dark:text-gray-100">{node.title}</span>
              {count != null && (
                <span
                  className={cn(
                    'ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums',
                    accent.badge,
                    count === 0 && 'opacity-50',
                  )}>
                  {count}
                </span>
              )}
            </div>
            <span className="truncate text-[11px] leading-tight text-gray-500 dark:text-gray-400">{subtitle}</span>
          </Tooltip>
        );
      })}

      {/* Labels on the gate's in/out arrows. */}
      {gates.map((g) => (
        <div
          key={`${g.id}-in`}
          className="absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded bg-white/85 px-1 text-[10px] leading-tight text-gray-400 dark:bg-slate-900/85 dark:text-gray-500"
          style={{ left: g.cx, top: (g.inFromY + g.box.y) / 2 }}>
          {g.inLabel}
        </div>
      ))}
      {gates.map((g) => (
        <div
          key={`${g.id}-out`}
          className="absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded bg-white/85 px-1 text-[10px] leading-tight text-gray-400 dark:bg-slate-900/85 dark:text-gray-500"
          style={{ left: g.cx, top: (g.box.y + g.box.h + g.outToY) / 2 }}>
          {g.outLabel}
        </div>
      ))}

      {/* Labels on the gate ⇄ Waiting-for-capacity arrows. */}
      {capacityLink != null && (
        <>
          <div
            className="absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded bg-white/85 px-1 text-[10px] leading-tight text-gray-400 dark:bg-slate-900/85 dark:text-gray-500"
            style={{ left: (capacityLink.boxRight + capacityLink.gateLeft) / 2, top: capacityLink.cy - 18 }}>
            no free slot
          </div>
          <div
            className="absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded bg-white/85 px-1 text-[10px] leading-tight text-gray-400 dark:bg-slate-900/85 dark:text-gray-500"
            style={{ left: (capacityLink.boxRight + capacityLink.gateLeft) / 2, top: capacityLink.cy + 18 }}>
            resumes
          </div>
        </>
      )}

      {/* Concurrency gates — a rounded node inline on each capped step's entry
          edge: only ≤5 can be in that step at once. */}
      {gates.map((g) => (
        <Tooltip
          key={g.id}
          as="div"
          tooltip={g.tip}
          tooltipMaxWidth={300}
          className="absolute flex cursor-help items-center justify-center gap-1.5 rounded-full border border-gray-300 bg-gray-50 px-3 shadow-sm dark:border-gray-600 dark:bg-gray-800"
          style={{ left: g.box.x, top: g.box.y, width: g.box.w, height: g.box.h }}>
          <General.Zap className="size-3.5 shrink-0 text-gray-400 dark:text-gray-500" />
          <span className="whitespace-nowrap text-[12px] font-medium text-gray-600 dark:text-gray-300">
            limit {CONCURRENCY_CAP} concurrent
          </span>
        </Tooltip>
      ))}
    </div>
  );
}
